import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LifeBuoy, MessagesSquare } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { useAdvertiser } from "./useAdvertiser";

export default function SupportPage() {
  useSeo({ title: "Support | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { me } = useAdvertiser();
  const { data, refetch } = useQuery({
    queryKey: ["advertiser", "tickets"],
    queryFn: async () => (await api.get("/advertiser/tickets")).data,
  });
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reply, setReply] = useState({});
  const [appealMessage, setAppealMessage] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);
  const [appealError, setAppealError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/advertiser/tickets", { subject, message });
      toast.success("Ticket opened");
      setSubject("");
      setMessage("");
      await refetch();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (id) => {
    const text = (reply[id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/advertiser/tickets/${id}/reply`, { message: text });
      setReply((r) => ({ ...r, [id]: "" }));
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const submitAppeal = async (e) => {
    e.preventDefault();
    setAppealError("");
    setAppealBusy(true);
    try {
      await api.post("/advertiser/appeals", { message: appealMessage });
      toast.success("Appeal submitted");
      setAppealMessage("");
      client.invalidateQueries({ queryKey: ["advertiser", "me"] });
    } catch (err) {
      setAppealError(errMsg(err));
    } finally {
      setAppealBusy(false);
    }
  };

  const canAppeal = me?.suspension && !me.suspension.appeal;
  const hasAppeal = !!me?.suspension?.appeal;

  return (
    <div className="space-y-8" data-testid="support-page">
      <header>
        <p className="lux-label">Support</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">We're here to help</h1>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">Open a ticket for anything you can't do from the dashboard. We aim to acknowledge within 2 working days.</p>
      </header>

      {me?.suspension && (
        <section className="lux-card border-lux-error/30 bg-lux-error/5 p-5 sm:p-6" data-testid="support-suspension-card">
          <p className="lux-label text-lux-error">Your account is suspended</p>
          <p className="mt-2 font-serif text-xl text-lux-text">Submit one appeal</p>
          <p className="mt-1 text-sm text-lux-text/85">Reason: {me.suspension.reason}</p>
          {hasAppeal ? (
            <div className="mt-4 rounded-[10px] border border-lux-border bg-lux-surface2 p-4" data-testid="support-appeal-existing">
              <p className="text-sm text-lux-text">Appeal status: <strong className="text-lux-silverBright">{me.suspension.appeal.status}</strong></p>
              {me.suspension.appeal.decision_note && <p className="mt-1 text-sm text-lux-muted">{me.suspension.appeal.decision_note}</p>}
              <p className="mt-1 text-xs text-lux-muted">Submitted {fmtDate(me.suspension.appeal.created_at, true)}</p>
            </div>
          ) : canAppeal ? (
            <form onSubmit={submitAppeal} className="mt-4 space-y-3">
              <label htmlFor="appeal-message" className="lux-label">Your appeal</label>
              <textarea id="appeal-message" rows={5} minLength={20} maxLength={2000} className="lux-input" value={appealMessage}
                onChange={(e) => setAppealMessage(e.target.value)} placeholder="Explain anything that will help staff reconsider." data-testid="support-appeal-textarea" />
              {appealError && <p className="text-sm text-lux-error" role="alert">{appealError}</p>}
              <button type="submit" disabled={appealBusy || appealMessage.trim().length < 20} className="lux-btn lux-btn-primary" data-testid="support-appeal-submit">
                {appealBusy ? "Submitting..." : "Submit appeal"}
              </button>
              <p className="text-xs text-lux-muted">A different member of staff will review your appeal.</p>
            </form>
          ) : null}
        </section>
      )}

      <section className="lux-card p-5 sm:p-6">
        <p className="lux-label">New ticket</p>
        <h2 className="mt-2 font-serif text-xl text-lux-text">Open a support ticket</h2>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="ticket-subject" className="lux-label">Subject</label>
            <input id="ticket-subject" required maxLength={120} className="lux-input mt-2" value={subject}
              onChange={(e) => setSubject(e.target.value)} placeholder="A short description" data-testid="support-ticket-subject-input" />
          </div>
          <div>
            <label htmlFor="ticket-message" className="lux-label">Message</label>
            <textarea id="ticket-message" rows={5} required minLength={10} maxLength={3000} className="lux-input mt-2" value={message}
              onChange={(e) => setMessage(e.target.value)} placeholder="Tell us what you need" data-testid="support-ticket-message-textarea" />
          </div>
          {error && <p className="text-sm text-lux-error" role="alert">{error}</p>}
          <button type="submit" disabled={busy} className="lux-btn lux-btn-primary" data-testid="support-ticket-submit">
            {busy ? "Opening..." : "Open ticket"}
          </button>
        </form>
      </section>

      <section>
        <p className="lux-label">Your tickets</p>
        <h2 className="mt-2 font-serif text-xl text-lux-text">History</h2>
        {!data?.tickets?.length ? (
          <div className="lux-card mt-4 flex flex-col items-center px-6 py-12 text-center" data-testid="support-tickets-empty">
            <LifeBuoy className="h-8 w-8 text-lux-silver" aria-hidden="true" />
            <p className="mt-4 font-serif text-lg text-lux-text">No tickets yet</p>
          </div>
        ) : (
          <ul className="mt-4 space-y-4">
            {data.tickets.map((t) => (
              <li key={t.id} className="lux-card p-5" data-testid="support-ticket-item">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-serif text-lg text-lux-text">{t.subject}</p>
                  <span className={`lux-chip text-xs ${t.status === "open" ? "text-lux-warning" : "text-lux-success"}`}>{t.status}</span>
                </div>
                <ul className="mt-3 space-y-3">
                  {t.messages.map((m, i) => (
                    <li key={i} className="rounded-[10px] border border-lux-border bg-lux-surface2 p-3">
                      <p className="text-xs text-lux-muted">{m.name} · {fmtDate(m.at, true)}</p>
                      <p className="mt-1 text-sm text-lux-text/90">{m.message}</p>
                    </li>
                  ))}
                </ul>
                {t.status === "open" && (
                  <div className="mt-3 flex gap-2">
                    <input className="lux-input" value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })}
                      placeholder="Reply..." data-testid="support-ticket-reply-input" />
                    <button onClick={() => sendReply(t.id)} className="lux-btn lux-btn-secondary" data-testid="support-ticket-reply-submit">
                      <MessagesSquare className="h-4 w-4" /> Send
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
